import type { ISize } from '@sixui/ui';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Stack } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { Helmet } from 'react-helmet-async';

import type { IAppRegion } from '~/lib/appSettings';
import { Logo } from '~/components/atoms/Logo';
import { PhoneSkin } from '~/components/atoms/PhoneSkin';
import { ColorSchemeSwitch } from '~/components/organisms/fields/ColorSchemeSwitch';
import { Navigate } from '~/components/utils/Navigation';
import { appRegionsSettings } from '~/lib/appSettings';

const PREVIEW_RATIO = 408 / 831;
const PREVIEW_WIDTH = 360;
const PREVIEW_HEIGHT = PREVIEW_WIDTH / PREVIEW_RATIO;
const PREVIEW_FULLSCREEN_BREAKPOINT = '@media (max-width: 480px)';

export type IAppPublicPreviewPageProps = {
  appUri: string;
  previousUrl?: string;
};

const styles = stylex.create({
  host: {
    width: '100%',
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
  },
  phone: {
    position: 'relative',
    width: {
      default: PREVIEW_WIDTH,
      [PREVIEW_FULLSCREEN_BREAKPOINT]: '100%',
    },
    height: {
      default: 'auto',
      [PREVIEW_FULLSCREEN_BREAKPOINT]: '100vh',
    },
    aspectRatio: {
      default: 408 / 831,
      [PREVIEW_FULLSCREEN_BREAKPOINT]: 'unset',
    },
  },
  header: {
    position: 'absolute',
    insetInline: 0,
    top: -64,
    display: 'flex',
    justifyContent: 'center',
  },
  leftPanel: {
    position: 'absolute',
    insetBlock: 0,
    left: -56,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: 160,
  },
  phoneScreen: {
    backgroundColor: colorSchemeTokens.surface,
    position: 'absolute',
    insetInline: {
      default: '5%',
      [PREVIEW_FULLSCREEN_BREAKPOINT]: '0',
    },
    insetBlock: {
      default: '2%',
      [PREVIEW_FULLSCREEN_BREAKPOINT]: '0',
    },
  },
  phoneSkin: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',

    display: {
      default: 'block',
      [PREVIEW_FULLSCREEN_BREAKPOINT]: 'none',
    },
  },
  iframe: (scale: number) => ({
    width: {
      default: `${(1 + (1 - scale) / scale) * 100}%`,
      [PREVIEW_FULLSCREEN_BREAKPOINT]: '100%',
    },
    height: {
      default: `${(1 + (1 - scale) / scale) * 100}%`,
      [PREVIEW_FULLSCREEN_BREAKPOINT]: '100vh',
    },
    scale: {
      default: scale,
      [PREVIEW_FULLSCREEN_BREAKPOINT]: '1',
    },
    transformOrigin: '0 0',
    backgroundColor: colorSchemeTokens.surface,
  }),
  footer: {
    position: 'absolute',
    insetInline: 0,
    bottom: -56,
  },
});

const getScaleFromSize = (size: ISize): number => 0.002 * size.width + 0.018;

export const AppPublicPreviewPage: React.FC<IAppPublicPreviewPageProps> = (
  props,
) => {
  const { appUri, previousUrl } = props;
  const [appId, appRegion] = appUri.split('.');
  const regionSettings = appRegionsSettings[appRegion as IAppRegion];

  if (!regionSettings) {
    return <Navigate to="/" />;
  }

  const { previewUrl } = regionSettings;
  const iframeUrl = `${previewUrl}/preview-generic/index.html?apiKey=appId:${appId}`;

  return (
    <>
      <Helmet>
        <title>Crowdaa</title>
      </Helmet>

      <div {...stylex.props(styles.host)}>
        <div {...stylex.props(styles.phone)}>
          <div {...stylex.props(styles.header)}>
            <Logo sx={styles.logo} />
          </div>

          {previousUrl ? (
            <div {...stylex.props(styles.leftPanel)}>
              <IconButton
                icon={<FontAwesomeIcon icon={faArrowLeft} />}
                href={previousUrl}
              />
            </div>
          ) : null}

          <div {...stylex.props(styles.phoneScreen)}>
            <iframe
              {...stylex.props(
                styles.iframe(
                  getScaleFromSize({
                    width: PREVIEW_WIDTH,
                    height: PREVIEW_HEIGHT,
                  }),
                ),
              )}
              title="App preview"
              src={iframeUrl}
            ></iframe>
          </div>
          <PhoneSkin sx={styles.phoneSkin} />

          <Stack
            horizontal
            gap={2}
            align="center"
            justify="center"
            sx={styles.footer}
          >
            <ColorSchemeSwitch />
          </Stack>
        </div>
      </div>
    </>
  );
};
